"use client";

import Link from "next/link";
import { FormEvent, useState } from "react";
import type { CountryCode } from "libphonenumber-js";
import { ArrowRight, MessageCircle } from "lucide-react";
import { AuthShell } from "@/components/auth-shell";
import { CountryDialPicker, PhoneDialRow } from "@/components/country-dial-picker";
import { usePrefs } from "@/components/prefs-provider";
import { Button } from "@/components/ui/button";
import { CodeInput, Field, Input, PasswordInput } from "@/components/ui/field";
import { InlineError } from "@/components/ui/feedback";

type Step = "form" | "verify";

function mapRegisterError(code: string, role: string, t: ReturnType<typeof usePrefs>["t"]) {
  const a = t.auth;
  const m: Record<string, string> = {
    phone_taken: role === "child" ? a.phoneTakenChild : a.phoneTaken,
    not_on_whatsapp: a.notOnWhatsapp,
    invalid_phone: a.invalidPhone,
    reporter_offline: a.reporterOffline,
    send_failed: a.codeSendFailed,
    whatsapp_check_failed: a.codeSendFailed,
    whatsapp_service_outdated: a.codeSendFailed,
    duplicate_send: a.duplicateSend,
    invalid_code: a.invalidCode,
    expired: a.codeExpired,
    too_many_attempts: a.tooManyAttempts,
    not_found: a.verificationExpired,
    password_mismatch: a.passwordMismatch,
  };
  return m[code] || a.registerFailed;
}

export default function RegisterPage() {
  const { t, locale } = usePrefs();
  const a = t.auth;
  const [step, setStep] = useState<Step>("form");
  const [country, setCountry] = useState<CountryCode>("IL");
  const [phone, setPhone] = useState("");
  const [displayPhone, setDisplayPhone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pwMismatch, setPwMismatch] = useState(false);

  async function onSubmitForm(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError("");
    setPwMismatch(false);
    const fd = new FormData(e.currentTarget);
    const password = String(fd.get("password") || "");
    const confirmPassword = String(fd.get("confirmPassword") || "");
    if (password !== confirmPassword) {
      setPwMismatch(true);
      setLoading(false);
      return;
    }
    try {
      const res = await fetch("/api/auth/register/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: fd.get("name"),
          phone: fd.get("phone"),
          country,
          password,
          confirmPassword,
          locale,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(mapRegisterError(data.error || "", data.role || "", t));
        return;
      }
      setPhone(String(data.phone || ""));
      setDisplayPhone(String(data.displayPhone || data.phone || ""));
      setStep("verify");
    } catch {
      setError(a.registerFailed);
    } finally {
      setLoading(false);
    }
  }

  async function onSubmitVerify(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError("");
    const fd = new FormData(e.currentTarget);
    try {
      const res = await fetch("/api/auth/register/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, code: fd.get("code") }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(mapRegisterError(data.error || "", data.role || "", t));
        setLoading(false);
        return;
      }
      window.location.assign("/onboarding");
    } catch {
      setError(a.registerFailed);
      setLoading(false);
    }
  }

  const footer = (
    <p>
      {a.haveAccount}{" "}
      <Link href="/login" className="font-semibold text-brand underline-offset-2 hover:underline">
        {a.login}
      </Link>
    </p>
  );

  if (step === "verify") {
    return (
      <AuthShell
        stepKey="verify"
        title={a.verifyWhatsappTitle}
        lead={
          <>
            {a.verifyWhatsappLead} <span className="ltr inline-block font-semibold text-ink">{displayPhone}</span>
          </>
        }
        footer={footer}
      >
        <form onSubmit={onSubmitVerify} className="space-y-5">
          <Field label={a.verificationCode} hint={a.codeHintWhatsapp} required>
            {(ctl) => <CodeInput {...ctl} name="code" required autoFocus placeholder="••••••" />}
          </Field>
          <InlineError message={error} />
          <Button type="submit" size="lg" className="w-full" loading={loading} icon={<MessageCircle className="h-4 w-4" aria-hidden />}>
            {loading ? a.verifying : a.confirmEmail}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => {
              setStep("form");
              setError("");
            }}
          >
            {a.wrongPhoneBack}
          </Button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell stepKey="form" title={a.registerTitleFull} lead={a.registerLead} footer={footer}>
      <form onSubmit={onSubmitForm} className="space-y-4">
        <Field label={a.fullName} required>
          {(ctl) => <Input {...ctl} name="name" required autoComplete="name" autoFocus maxLength={80} />}
        </Field>
        <Field label={a.accountPhone} hint={a.accountPhoneHint} required>
          {(ctl) => (
            <PhoneDialRow>
              <CountryDialPicker value={country} onChange={setCountry} label={t.dash.parentCountry} />
              <Input
                {...ctl}
                name="phone"
                type="tel"
                ltr
                required
                autoComplete="tel-national"
                inputMode="numeric"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder="050-000-0000"
                className="min-w-0 flex-1"
              />
            </PhoneDialRow>
          )}
        </Field>
        <Field label={a.password} hint={a.passwordHint} required>
          {(ctl) => (
            <PasswordInput
              {...ctl}
              name="password"
              required
              minLength={6}
              autoComplete="new-password"
              showLabel={t.common.showPassword}
              hideLabel={t.common.hidePassword}
            />
          )}
        </Field>
        <Field label={a.confirmPassword} error={pwMismatch ? a.passwordMismatch : undefined} required>
          {(ctl) => (
            <PasswordInput
              {...ctl}
              name="confirmPassword"
              required
              minLength={6}
              autoComplete="new-password"
              onChange={() => setPwMismatch(false)}
              showLabel={t.common.showPassword}
              hideLabel={t.common.hidePassword}
            />
          )}
        </Field>
        <InlineError message={error} />
        <Button type="submit" size="lg" className="w-full" loading={loading} icon={<ArrowRight className="h-4 w-4 rtl:-scale-x-100" aria-hidden />}>
          {loading ? a.sendingCode : a.continueRegister}
        </Button>
        <p className="text-xs leading-relaxed text-ink-3">
          {a.termsNote}{" "}
          <Link href="/terms" className="font-semibold text-ink-2 underline underline-offset-2">
            {t.footer.terms}
          </Link>{" "}
          {t.common.and}{" "}
          <Link href="/privacy" className="font-semibold text-ink-2 underline underline-offset-2">
            {t.footer.privacy}
          </Link>
          .
        </p>
      </form>
    </AuthShell>
  );
}
